import { Box, Chip, MenuItem, MenuItemProps, Select, SelectProps, styled } from '@mui/material';

import { CerebroCheckbox } from './CerebroCheckbox';
import { CerebroDropdownIcon } from './CerebroDropdownIcon';
import { fontFamily, weights } from '../context/CerebroThemeProvider';

export const CerebroMultiDropdown: React.FC<SelectProps> = props => {
	const labels = (props.children as any[] | undefined)?.reduce(
		(acc, current) => ({ ...acc, [current.props.value]: current.props.children }),
		{}
	);

	return (
		<StyledSelect
			multiple
			displayEmpty
			IconComponent={CerebroDropdownIcon}
			MenuProps={{
				PaperProps: {
					sx: theme => ({
						backgroundColor: theme.palette.gray.background,
						boxShadow: 'none',
						border: `1px solid ${theme.palette.gray.dark}`,
					}),
				},
			}}
			renderValue={selected => {
				if ((selected as unknown[]).length === 0) {
					return props.placeholder;
				}

				return (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{Array.isArray(selected) && selected.map(value => <Chip key={value} label={labels[value]} />)}
					</Box>
				);
			}}
			{...props}
		/>
	);
};

export const CerebroMultiDropdownMenuItem: React.FC<MenuItemProps> = ({ children, ...props }) => (
	<StyledMenuItem {...props}>
		<CerebroCheckbox value={props.selected === true} size="small" sx={{ padding: 0, paddingRight: 1 }} />
		{children}
	</StyledMenuItem>
);

const StyledSelect = styled(Select)(({ error, disabled, readOnly, theme }) => ({
	borderWidth: 2,
	borderStyle: 'solid',
	borderRadius: theme.shape.borderRadius,
	borderColor: error
		? theme.palette.error.main
		: disabled || readOnly
		? theme.palette.gray.light
		: theme.palette.gray.medium,

	color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,

	'&, & *': {
		cursor: disabled ? 'not-allowed' : 'initial',
	},

	'& svg': {
		color: readOnly || disabled ? theme.palette.gray.light : theme.palette.gray.dark,
	},

	fontFamily,
	fontSize: 12,
	fontWeight: disabled ? weights.medium : weights.semibold,

	lineHeight: '14px',

	marginTop: theme.spacing(1.5),
	marginBottom: theme.spacing(1),

	outline: 'none',

	width: '100%',

	'&:focus-within': {
		borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
	},

	'& .MuiSelect-select': {
		padding: '14px 12px',
	},

	'& fieldset': {
		border: 'none',
	},

	'& .MuiChip-root': {
		backgroundColor: disabled || readOnly ? theme.palette.gray.light : theme.palette.primary.main,
	},
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	fontFamily,
	fontSize: 12,

	lineHeight: '14px',

	'&.Mui-selected': {
		backgroundColor: 'transparent',
	},

	'&.Mui-selected:hover': {
		backgroundColor: theme.palette.action.hover,
	},
}));
